import { Option } from 'types/Generics';
import { IntakeFormPreferences } from 'types/IntakeForm';
import { OnlineBookingPreferences } from 'types/OnlineBooking';
import { PetSize, PetTypeId, PetTypeIds } from 'types/Pet';

export interface Marketplace {
    id: number;
    slug: string;
    name?: string;
    logo?: string;
    phone?: string;
    timeZone: string;

    businessType: MarketplaceType;

    intakeFormPreferences: IntakeFormPreferences;
    onlineBookingPreferences: OnlineBookingPreferences;

    address?: MarketplaceAddress;

    deposit?: MarketplaceDeposit;

    specialHours: Array<SpecialHour>;

    vaccines: Array<Option<string>>;

    catsEnabled?: boolean;
    dogsEnabled?: boolean;
    exoticsEnabled?: boolean;

    petTypes: MarketplacePetTypes;

    scheduleType: MarketplaceScheduleType;
    schedule: MarketplaceSchedule;
}

export interface MarketplaceDeposit {
    value: number;
    category: MarketplaceDepositCategory;
}

export enum MarketplaceDepositCategory {
    NONE = 'none',
    FIXED = 'amount',
    PERCENTAGE = 'percentage'
}

export interface SpecialHour {
    id: number;
    name: string;
    date: string;
    closed: boolean;
    businessHours: {
        open: string;
        close: string;
    };
}

export enum MarketplaceType {
    INHOME = 'inhome',
    MOBILE = 'mobile',
    ATHOME = 'athome',
    LOCAL_BUSINESS = 'b&m'
}

export interface MarketplaceAddress {
    id: number;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
    latitude: number;
    longitude: number;
}

export enum MarketplaceScheduleType {
    Work = 'work_schedule',
    MultiSlots = 'multi_slots'
}

export const emptyMarketplace: Marketplace = {
    id: 0,
    slug: '',
    timeZone: 'UTC',

    businessType: MarketplaceType.LOCAL_BUSINESS,

    logo: '',
    name: '',
    phone: '',

    address: undefined,

    specialHours: [],

    vaccines: [],

    onlineBookingPreferences: {
        enabled: false,
        bookingDateLimits: {
            from: null,
            to: null
        },
        allowNewClients: false,
        allowSelectStaff: false,
        requireBookingRequest: false,
        promoImageUrl: '',
        pricingOption: 'starting_at'
    },

    intakeFormPreferences: {
        enabled: false,
        questionsEnabled: false,
        questions: []
    },

    petTypes: {
        [PetTypeIds.DOG]: {
            enabled: false,
            sizes: [],
            hairTypes: [],
            hairLengths: []
        },
        [PetTypeIds.CAT]: {
            enabled: false,
            sizes: [],
            hairTypes: [],
            hairLengths: []
        },
        [PetTypeIds.EXOTIC]: {
            enabled: false,
            sizes: [],
            hairTypes: [],
            hairLengths: []
        }
    },

    scheduleType: MarketplaceScheduleType.Work,
    schedule: {
        monday: null,
        tuesday: null,
        wednesday: null,
        thursday: null,
        friday: null,
        saturday: null,
        sunday: null
    }
};

export interface MarketplaceTypeDogs {
    ages: Array<Option<string>>;
    sizes: Array<MarketplacePetSize>;
    hairTypes: Array<Option<string>>;
    hairLengths: Array<Option<string>>;
}

export interface MarketplaceTypeCats {
    ages: Array<Option<string>>;
    sizes?: Array<MarketplacePetSize>;
    hairTypes: Array<Option<string>>;
    hairLengths: Array<Option<string>>;
}

export interface MarketplaceTypeExotics {
    types: Array<Option<string>>;
    others: Array<string>;
    sizes?: Array<MarketplacePetSize>;
}

export interface MarketplacePetSize {
    id: string;
    name: string;
    description?: string;
    minWeight: number | null;
    maxWeight: number | null;
}

export interface MarketplaceSchedule {
    sunday: MarketplaceScheduleRange;
    monday: MarketplaceScheduleRange;
    tuesday: MarketplaceScheduleRange;
    wednesday: MarketplaceScheduleRange;
    thursday: MarketplaceScheduleRange;
    friday: MarketplaceScheduleRange;
    saturday: MarketplaceScheduleRange;
}

export type MarketplaceScheduleRange = {
    open: string;
    close: string;
} | null;

export type MarketplacePetTypes = Record<PetTypeId, MarketplacePetType>;

export interface MarketplacePetType {
    enabled: boolean;
    sizes: Array<PetSize>;
    hairTypes: Array<Option<string>>;
    hairLengths: Array<Option<string>>;
}
